import {
  Box,
  Text,
  Skeleton,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  HStack,
  Button,
  Spacer,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import {
  CollateralDataQualityCheckReportingDatesBadge,
  CollateralDataQualityCheckResultActionsMenu,
  CollateralDataQualityCheckResultBadge,
  LastCheckedBadge,
} from './CollateralDataQualityCheckComponents';
import {
  CollateralDataQualityResults,
  CollateralDataQualityViewMode,
  currentReportingDateColumnName,
  previousReportingDateColumnName,
} from './utils';
import { useGetAthenaQueryResponseByRawQuery } from '~/packages/athena/requests/requests.gql.generated';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';

type CollateralDataQualityCheckResultProps = {
  cdqConfigId: string;
  cdqConfigName: string;
  cdqConfigQuery: string;
  organizationId: string;
  setViewMode: React.Dispatch<
    React.SetStateAction<CollateralDataQualityViewMode>
  >;
  setSelectedQueryResults: React.Dispatch<
    React.SetStateAction<CollateralDataQualityResults | undefined>
  >;
  handleArchive: ({ configId }: { configId: string }) => Promise<void>;
  isDeactivateCdqConfigLoading: boolean;
};
export const CollateralDataQualityCheckResult = ({
  cdqConfigId,
  cdqConfigName,
  cdqConfigQuery,
  organizationId,
  setViewMode,
  setSelectedQueryResults,
  handleArchive,
  isDeactivateCdqConfigLoading,
}: CollateralDataQualityCheckResultProps) => {
  const [lastAthenaQueryCompletedAt, setLastAthenaQueryCompletedAt] = useState<
    Date | undefined
  >();

  const {
    data: athenaQueryData,
    loading: isAthenaQueryLoading,
    error: athenaQueryError,
  } = useGetAthenaQueryResponseByRawQuery({
    variables: {
      organizationId,
      query: cdqConfigQuery,
      bindings: [],
      variables: [],
    },
    onCompleted: (data) => {
      if (data && data.athenaQueryResponse) {
        const currentTime = new Date();
        setLastAthenaQueryCompletedAt(currentTime);
      }
    },
  });

  const onClickViewDetail = useCallback(() => {
    if (athenaQueryData?.athenaQueryResponse.Items.length) {
      setSelectedQueryResults({
        cdqConfigName,
        queryResults: athenaQueryData.athenaQueryResponse.Items,
      });

      setViewMode('detail');
    }
  }, [
    setSelectedQueryResults,
    setViewMode,
    athenaQueryData?.athenaQueryResponse.Items,
    cdqConfigName,
  ]);

  const onClickArchiveConfig = useCallback(async () => {
    await handleArchive({ configId: cdqConfigId });
  }, [handleArchive, cdqConfigId]);

  const reportingDates = useMemo(() => {
    if (!athenaQueryData || !athenaQueryData.athenaQueryResponse.Items.length) {
      return {
        prevReportingDates: [],
        currentReportingDates: [],
      };
    }
    const queryDataItems = athenaQueryData.athenaQueryResponse.Items;
    const prevReportingDates = queryDataItems
      .map((queryDataItem) => queryDataItem[previousReportingDateColumnName])
      .filter((reportingDate) => !!reportingDate);
    const currentReportingDates = queryDataItems
      .map((queryDataItem) => queryDataItem[currentReportingDateColumnName])
      .filter((reportingDate) => !!reportingDate);

    return {
      prevReportingDates,
      currentReportingDates,
    };
  }, [athenaQueryData]);

  if (isAthenaQueryLoading || isDeactivateCdqConfigLoading) {
    return <Skeleton h="129px" />;
  }

  return (
    <Box
      p="24px"
      borderColor={colors.gray[200]}
      borderRadius="8px"
      borderWidth="1px"
    >
      <Grid templateColumns="repeat(3, 1fr)">
        <GridItem colSpan={2}>
          <Text fontWeight={500} mb="24px">
            {cdqConfigName}
          </Text>
          {!athenaQueryError && (
            <HStack alignItems="center" spacing="16px">
              <CollateralDataQualityCheckResultBadge
                numberOfFailures={
                  athenaQueryData?.athenaQueryResponse.Items.length ?? 0
                }
                badgeSize="small"
              />
              {!!reportingDates.currentReportingDates.length &&
                !!reportingDates.prevReportingDates.length && (
                  <CollateralDataQualityCheckReportingDatesBadge
                    prevReportingDates={reportingDates.prevReportingDates}
                    currentReportingDates={reportingDates.currentReportingDates}
                  />
                )}
            </HStack>
          )}
        </GridItem>
        <GridItem colSpan={1}>
          <HStack spacing="24px">
            <Spacer />
            {lastAthenaQueryCompletedAt && (
              <LastCheckedBadge date={lastAthenaQueryCompletedAt} />
            )}
            <HStack spacing="4px">
              <CollateralDataQualityCheckResultActionsMenu
                onClickArchiveConfig={onClickArchiveConfig}
                athenaQueryDataItems={
                  athenaQueryData?.athenaQueryResponse.Items
                }
                cdqConfigName={cdqConfigName}
              />
              {!!athenaQueryData?.athenaQueryResponse.Items.length && (
                <Button variant="ghost" onClick={onClickViewDetail} px="4px">
                  <Icon icon="chevron-right" />
                </Button>
              )}
            </HStack>
          </HStack>
        </GridItem>
      </Grid>
      {athenaQueryError && (
        <Alert status="error">
          <AlertIcon />
          {athenaQueryError.message}
        </Alert>
      )}
    </Box>
  );
};
